/**
 * Сотрудники — все юзеры с выплатами по месяцам за выбранный год.
 * Группировка: по умолчанию «Все», можно по отделу или должности.
 * Колонки: фото + имя + позиция, 12 месяцев, Σ за год.
 * Подсветка отдельно ЗП (категория FOT) от прочих выплат.
 */
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchStaffPayments, addStaffPayment, fetchRecommendedPayments,
  fetchUserPayments, deleteUserPayment,
  type StaffPaymentRow,
} from "@/api/finance";
import { FinanceLayout } from "@/components/finance/FinanceLayout";
import { Avatar } from "@/components/prototype/Avatar";
import { Icon } from "@/components/prototype/Icon";
import { useTaxonomy } from "@/lib/useTaxonomy";

const MONTHS = ["01","02","03","04","05","06","07","08","09","10","11","12"];
const MONTH_RU = ["Янв","Фев","Мар","Апр","Май","Июн","Июл","Авг","Сен","Окт","Ноя","Дек"];

function formatRub(v: number): string {
  if (!v) return "";
  return v.toLocaleString("ru-RU", { maximumFractionDigits: 0 }) + " ₽";
}

export function FinanceStaffPage() {
  const today = new Date();
  const [year, setYear] = useState<number>(today.getFullYear());
  const [groupBy, setGroupBy] = useState<"none" | "department" | "role">("none");
  const [showZero, setShowZero] = useState<boolean>(false);
  const [showOnlyFot, setShowOnlyFot] = useState<boolean>(false);
  const [payModalOpen, setPayModalOpen] = useState<{ userId: string; userName: string; suggestedDate: string; year: number; month: number; defaultAmount?: number } | null>(null);
  const qc = useQueryClient();

  const addPayMut = useMutation({
    mutationFn: (args: { user_id: string; date: string; amount: number; note?: string | null }) => addStaffPayment(args),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["finance-staff-payments"] });
      setPayModalOpen(null);
    },
    onError: (e: Error) => alert("Ошибка: " + e.message),
  });

  const q = useQuery({
    queryKey: ["finance-staff-payments", year],
    queryFn: () => fetchStaffPayments(year),
    staleTime: 60_000,
  });
  const recQ = useQuery({
    queryKey: ["finance-staff-recommended", year],
    queryFn: () => fetchRecommendedPayments(year),
    staleTime: 60_000,
  });
  const taxQ = useTaxonomy();
  const roleLabels: Record<string, string> = {};
  for (const r of (taxQ.data?.roles ?? [])) roleLabels[r.key] = r.label;
  const deptLabels: Record<string, string> = {};
  for (const d of (taxQ.data?.departments ?? [])) deptLabels[d.key] = d.label;

  const users = q.data?.users ?? [];
  const visibleUsers = useMemo(() => {
    if (showZero) return users;
    return users.filter(u => (showOnlyFot ? u.year_fot : u.year_total) > 0);
  }, [users, showZero, showOnlyFot]);

  const groups: { key: string; label: string; users: StaffPaymentRow[] }[] = useMemo(() => {
    if (groupBy === "none") {
      return [{ key: "all", label: "Все сотрудники", users: visibleUsers }];
    }
    const map = new Map<string, StaffPaymentRow[]>();
    for (const u of visibleUsers) {
      if (groupBy === "role") {
        const k = u.is_superuser ? "owner" : (u.role || "—");
        (map.get(k) || map.set(k, []).get(k)!).push(u);
      } else {
        const ds = u.departments && u.departments.length > 0 ? u.departments : ["—"];
        for (const d of ds) {
          (map.get(d) || map.set(d, []).get(d)!).push(u);
        }
      }
    }
    const lookup = groupBy === "role" ? roleLabels : deptLabels;
    return Array.from(map.entries())
      .map(([k, us]) => ({ key: k, label: lookup[k] || k, users: us }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [visibleUsers, groupBy, roleLabels, deptLabels]);

  const totalsPerMonth: Record<string, number> = {};
  let grandTotal = 0;
  for (const u of visibleUsers) {
    const src = showOnlyFot ? u.monthly_fot : u.monthly_total;
    for (const mm of MONTHS) {
      const v = src[mm] || 0;
      totalsPerMonth[mm] = (totalsPerMonth[mm] || 0) + v;
    }
    grandTotal += showOnlyFot ? u.year_fot : u.year_total;
  }

  return (
    <FinanceLayout>
      
      <div className="filter-bar" style={{ flexWrap: "wrap", gap: 8, marginBottom: "var(--gap)", alignItems: "center" }}>
        <label style={{ fontSize: 12, color: "var(--mute)" }}>Год:</label>
        <select className="input-sm" value={year} onChange={e => setYear(Number(e.target.value))}>
          {[today.getFullYear() - 1, today.getFullYear(), today.getFullYear() + 1].map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <span style={{ flex: "0 0 14px" }}/>
        <label style={{ fontSize: 12, color: "var(--mute)" }}>Группировка:</label>
        <div className="chip-row">
          <button className={`chip ${groupBy === "none" ? "active" : ""}`} onClick={() => setGroupBy("none")}>Без</button>
          <button className={`chip ${groupBy === "department" ? "active" : ""}`} onClick={() => setGroupBy("department")}>По отделу</button>
          <button className={`chip ${groupBy === "role" ? "active" : ""}`} onClick={() => setGroupBy("role")}>По должности</button>
        </div>
        <span style={{ flex: "0 0 14px" }}/>
        <button className={`chip ${showOnlyFot ? "active" : ""}`}
                onClick={() => setShowOnlyFot(v => !v)}
                title="Показывать только выплаты с категорией ФОТ (зарплата), исключая прочие">
          {showOnlyFot && <Icon name="check" size={11}/>} Только ФОТ-категория
        </button>
        <button className={`chip ${showZero ? "active" : ""}`}
                onClick={() => setShowZero(v => !v)}>
          {showZero && <Icon name="check" size={11}/>} Показать без выплат
        </button>
      </div>

      {q.isLoading && <div className="muted" style={{ padding: 20 }}>Загрузка…</div>}
      {q.error && <div className="alert alert-red"><div className="alert-body">{(q.error as Error).message}</div></div>}

      {!q.isLoading && !q.error && (
        <div className="card">
          <div style={{ overflowX: "auto" }}>
            <table className="tbl tbl-tight" style={{ fontSize: 12, minWidth: 1200 }}>
              <thead>
                <tr>
                  <th style={{ minWidth: 220, position: "sticky", left: 0, background: "var(--card)", zIndex: 2 }}>Сотрудник</th>
                  {MONTHS.map((mm, i) => (
                    <th key={mm} className="num" style={{ minWidth: 75 }}>{MONTH_RU[i]}</th>
                  ))}
                  <th className="num" style={{ minWidth: 110, background: "var(--paper-2)" }}>За год</th>
                </tr>
              </thead>
              <tbody>
                {groups.map(g => (
                  <>
                    {groupBy !== "none" && (
                      <tr key={`g-${g.key}`}>
                        <td colSpan={14} style={{
                          fontSize: 11, fontWeight: 600, color: "var(--mute)",
                          textTransform: "uppercase", letterSpacing: 0.4,
                          background: "var(--paper-2)", padding: "8px 12px",
                        }}>
                          {g.label} <span style={{ color: "var(--ink-3)", fontWeight: 400 }}>· {g.users.length}</span>
                        </td>
                      </tr>
                    )}
                    {g.users.map(u => {
                      const src = showOnlyFot ? u.monthly_fot : u.monthly_total;
                      const yearSum = showOnlyFot ? u.year_fot : u.year_total;
                      return (
                        <tr key={u.user_id}>
                          <td style={{ position: "sticky", left: 0, background: "var(--card)", zIndex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <Avatar name={u.display_name || u.email} userId={u.user_id}
                                      avatarFilename={u.avatar_filename} size={28} color="#4fae4e"/>
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <div style={{ fontWeight: 500, color: "var(--ink-1)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                  {u.display_name || u.email}
                                </div>
                                <div style={{ fontSize: 10, color: "var(--mute)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                  {u.position || (u.is_superuser ? "Собственник" : (roleLabels[u.role] || u.role))}
                                </div>
                              </div>

                            </div>
                          </td>
                          {MONTHS.map(mm => {
                            const hasPayment = mm in src;
                            const paid = src[mm];
                            const recommended = recQ.data?.recommended[u.user_id]?.[mm];
                            // Если есть запись о выплате (даже 0) — показываем как факт.
                            if (hasPayment) {
                              return (
                                <td key={mm} className="num tabular" style={{
                                  color: paid > 0 ? "var(--ink-1)" : "var(--mute)", fontWeight: 500,
                                  cursor: "pointer",
                                }}
                                onClick={() => setPayModalOpen({
                                  userId: u.user_id,
                                  userName: u.display_name || u.email,
                                  suggestedDate: `${year}-${mm}-15`,
                                  year, month: Number(mm),
                                })}
                                title="Редактировать выплаты за этот месяц">
                                  {paid > 0 ? formatRub(paid) : "0 ₽"}
                                </td>
                              );
                            }
                            if (recommended) {
                              return (
                                <td key={mm} className="num tabular" style={{
                                  color: "var(--brick)", fontWeight: 600, cursor: "pointer",
                                  background: "rgba(176,49,49,0.06)",
                                }}
                                onClick={() => setPayModalOpen({
                                  userId: u.user_id,
                                  userName: u.display_name || u.email,
                                  suggestedDate: `${year}-${mm}-15`,
                                  year, month: Number(mm),
                                  defaultAmount: recommended,
                                })}
                                title="Рекомендовано к выплате (по ФОТ). Клик — подтвердить или изменить.">
                                  {formatRub(recommended)}
                                </td>
                              );
                            }
                            return (
                              <td key={mm} className="num tabular" style={{
                                color: "var(--mute)", fontWeight: 400, cursor: "pointer",
                              }}
                              onClick={() => setPayModalOpen({
                                userId: u.user_id,
                                userName: u.display_name || u.email,
                                suggestedDate: `${year}-${mm}-15`,
                                year, month: Number(mm),
                              })}
                              title="Записать выплату">
                                —
                              </td>
                            );
                          })}
                          <td className="num tabular strong" style={{ background: "var(--paper-2)", color: "var(--green)" }}>
                            {yearSum ? formatRub(yearSum) : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </>
                ))}
                {/* Итого */}
                <tr style={{ borderTop: "2px solid var(--line)", fontWeight: 700, background: "var(--paper-2)" }}>
                  <td style={{ position: "sticky", left: 0, background: "var(--paper-2)", zIndex: 1, padding: "10px 12px", textTransform: "uppercase", letterSpacing: 0.3, fontSize: 11 }}>
                    Итого ({visibleUsers.length})
                  </td>
                  {MONTHS.map(mm => (
                    <td key={mm} className="num tabular" style={{ color: "var(--ink-1)" }}>
                      {totalsPerMonth[mm] ? formatRub(totalsPerMonth[mm]) : "—"}
                    </td>
                  ))}
                  <td className="num tabular" style={{ color: "var(--green)", fontSize: 14 }}>
                    {grandTotal ? formatRub(grandTotal) : "—"}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {payModalOpen && (
        <StaffPaymentModal
          userId={payModalOpen.userId}
          userName={payModalOpen.userName}
          defaultDate={payModalOpen.suggestedDate}
          defaultAmount={payModalOpen.defaultAmount}
          year={payModalOpen.year}
          month={payModalOpen.month}
          saving={addPayMut.isPending}
          onSubmit={(payload) => addPayMut.mutate(payload)}
          onClose={() => setPayModalOpen(null)}
          onRefetch={() => {
            qc.invalidateQueries({ queryKey: ["finance-staff-payments"] });
            qc.invalidateQueries({ queryKey: ["finance-user-payments", payModalOpen?.userId, payModalOpen?.year, payModalOpen?.month] });
          }}
        />
      )}
    </FinanceLayout>
  );
}


function StaffPaymentModal({ userId, userName, defaultDate, defaultAmount, year, month, saving, onSubmit, onClose, onRefetch }: {
  userId: string;
  userName: string;
  defaultDate: string;
  defaultAmount?: number;
  year: number;
  month: number;
  saving: boolean;
  onSubmit: (p: { user_id: string; date: string; amount: number; note?: string | null }) => void;
  onClose: () => void;
  onRefetch: () => void;
}) {
  const [date, setDate] = useState(defaultDate);
  const [amount, setAmount] = useState<string>(defaultAmount ? String(Math.round(defaultAmount)) : "");
  const [note, setNote] = useState<string>("");
  const valid = !!date && Number(amount) >= 0 && amount !== "";

  // Существующие выплаты за этот месяц
  const existingQ = useQuery({
    queryKey: ["finance-user-payments", userId, year, month],
    queryFn: () => fetchUserPayments(userId, year, month),
    staleTime: 0,
  });
  const [deleting, setDeleting] = useState<number | null>(null);
  const doDelete = async (id: number) => {
    if (!confirm("Удалить эту запись о выплате?")) return;
    setDeleting(id);
    try {
      await deleteUserPayment(id);
      onRefetch();
    } catch (e) {
      alert("Ошибка: " + (e as Error).message);
    } finally {
      setDeleting(null);
    }
  };
  const existing = existingQ.data?.items ?? [];
  const existingTotal = existing.reduce((s, p) => s + p.amount, 0);
  return (
    <div onClick={onClose}
         style={{
           position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
           display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
         }}>
      <div onClick={(e) => e.stopPropagation()}
           style={{
             background: "var(--card)", borderRadius: 12, padding: 24, minWidth: 360,
             boxShadow: "0 12px 40px rgba(0,0,0,0.3)",
           }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Записать выплату</div>
        <div style={{ fontSize: 13, color: "var(--mute)", marginBottom: 16 }}>
          Сотрудник: <strong style={{ color: "var(--ink-1)" }}>{userName}</strong>
          {defaultAmount !== undefined && (
            <div style={{ marginTop: 6, padding: "6px 10px", background: "rgba(176,49,49,0.08)", borderRadius: 6, fontSize: 12, color: "var(--brick)" }}>
              Рекомендовано по ФОТ: <strong>{Math.round(defaultAmount).toLocaleString("ru-RU")} ₽</strong>
            </div>
          )}
        </div>

        {/* Existing payments list */}
        {existing.length > 0 && (
          <div style={{ marginBottom: 16, padding: 10, background: "var(--paper-2)", borderRadius: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 8 }}>
              Выплаты в этом месяце ({existing.length}, итого {existingTotal.toLocaleString("ru-RU")} ₽)
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {existing.map(p => (
                <div key={p.id} style={{
                  display: "flex", alignItems: "center", gap: 8, fontSize: 12,
                  padding: "6px 8px", background: "var(--card)", borderRadius: 4,
                }}>
                  <span className="tabular" style={{ color: "var(--mute)" }}>{p.date}</span>
                  <span style={{ flex: 1, color: "var(--ink-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {p.description || "—"}
                  </span>
                  <strong style={{ color: "var(--ink-1)", fontVariantNumeric: "tabular-nums" }}>
                    {Math.round(p.amount).toLocaleString("ru-RU")} ₽
                  </strong>
                  <button type="button" onClick={() => doDelete(p.id)} disabled={deleting === p.id}
                          title="Удалить"
                          style={{
                            background: "transparent", border: "none", color: "var(--brick)",
                            cursor: "pointer", padding: "0 4px", fontSize: 16, lineHeight: 1,
                          }}>
                    {deleting === p.id ? "…" : "×"}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ fontSize: 11, color: "var(--mute)", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.3 }}>
          {existing.length > 0 ? "Добавить ещё одну выплату" : "Новая выплата"}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "var(--mute)" }}>
            Дата выплаты
            <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)}/>
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "var(--mute)" }}>
            Сумма (₽)
            <input className="input" type="number" value={amount} step={1000} placeholder="50000"
                   onChange={(e) => setAmount(e.target.value)} autoFocus/>
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "var(--mute)" }}>
            Комментарий (опционально)
            <input className="input" type="text" value={note} placeholder="Зарплата за апрель"
                   onChange={(e) => setNote(e.target.value)}/>
          </label>
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 18 }}>
          <button className="btn btn-sm" onClick={onClose} disabled={saving}>Отмена</button>
          <button className="btn btn-sm btn-primary"
                  onClick={() => onSubmit({
                    user_id: userId, date, amount: Number(amount),
                    note: note.trim() || null,
                  })}
                  disabled={!valid || saving}>
            {saving ? "Сохранение…" : (defaultAmount !== undefined ? "Подтвердить" : "Записать")}
          </button>
        </div>
      </div>
    </div>
  );
}
